import { AsyncComponentDataStatus, DailyAgendaState } from '../../state/types';
import { DailyAgendaActionFactory, DailyAgendaActions } from '../actions';
import {
  listEventTimeSlots,
  queryExtendedBookings,
} from '../../../../adapters';
import type { IHttpError } from '@wix/http-client';

import { PagingMetadataV2 } from '@wix/ambassador-bookings-reader-v2-extended-booking/types';

export const createOnLoadMoreAction: DailyAgendaActionFactory<
  DailyAgendaActions['onLoadMore']
> = ({ setState, getState, flowAPI }) => {
  const onLoadMoreAction: DailyAgendaActions['onLoadMore'] = async () => {
    const { slots, slotsMetaData, bookedSlotsMetaData, bookedSlots } =
      getState();

    if (
      slots.status !== AsyncComponentDataStatus.IDLE ||
      slotsMetaData.status !== AsyncComponentDataStatus.IDLE ||
      !slotsMetaData.data?.hasNext
    ) {
      return;
    }

    const slotsCursor = slotsMetaData.data?.cursors?.next!;
    const bookedSlotsCursor = (bookedSlotsMetaData.data as PagingMetadataV2)
      .cursors?.next!;

    setState({
      slotsMetaData: { status: AsyncComponentDataStatus.LOADING },
      bookedSlotsMetaData: { status: AsyncComponentDataStatus.LOADING },
    });

    try {
      const [
        { timeSlots, pagingMetadata },
        { extendedBookings, pagingMetadata: bookedSlotsPagingMetadata },
      ] = await Promise.all([
        listEventTimeSlots({
          flowAPI,
          cursor: slotsCursor,
        }),
        bookedSlotsCursor
          ? queryExtendedBookings({
              flowAPI,
              cursor: bookedSlotsCursor,
            })
          : { extendedBookings: [], pagingMetadata: {} },
      ]);

      const bookedSlotsState: Partial<DailyAgendaState> = {
        bookedSlots: {
          status: AsyncComponentDataStatus.IDLE,
          data: [
            ...(bookedSlots.data as string[]),
            ...(extendedBookings || [])?.map(
              (extendedBooking) =>
                extendedBooking.booking?.bookedEntity?.slot?.eventId!,
            ),
          ],
        },
        bookedSlotsMetaData: {
          status: AsyncComponentDataStatus.IDLE,
          data: bookedSlotsPagingMetadata,
        },
      };

      if (!timeSlots || timeSlots.length === 0) {
        setState({
          slotsMetaData: {
            status: AsyncComponentDataStatus.ERROR,
            data: 'No more slots found',
          },
          ...bookedSlotsState,
        });
        return;
      }

      setState({
        slots: {
          status: AsyncComponentDataStatus.IDLE,
          data: [...(slots.data || []), ...timeSlots],
        },
        slotsMetaData: {
          status: AsyncComponentDataStatus.IDLE,
          data: pagingMetadata,
        },
        ...bookedSlotsState,
      });
    } catch (error) {
      setState({
        slotsMetaData: {
          status: AsyncComponentDataStatus.ERROR,
          data: (error as IHttpError).response?.data,
        },
      });
      console.error('Error loading more slots', error);
    }
  };

  return onLoadMoreAction;
};
